import { datadogRum } from '@datadog/browser-rum';
import * as amplitude from '@amplitude/analytics-browser';
import FlagProvider from '@unleash/proxy-client-react';

import './index.scss';
import App from './App';
import buildInfo from './build-info.json';
import { unleashConfig } from './services/feature-flag';
import {
  DEVELOPMENT_AMPLITUDE_KEY,
  PRODUCTION_AMPLITUDE_KEY,
} from 'constants/amplitude';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SESSION_KEY } from 'utils/session';
import { BrowserRouter } from 'react-router-dom';
import { ChakraProvider, defaultSystem } from '@chakra-ui/react';
import { createRoot } from 'react-dom/client';
import { StrictMode } from 'react';

const IS_PROD = process.env.NODE_ENV === 'production';

if (IS_PROD) {
  datadogRum.init({
    applicationId: 'b2dfe768-2769-4946-8eff-8cefd5c65b8e',
    clientToken: 'pub48ee1f6dd1ed500020bf62c218aedb31',
    site: 'datadoghq.com',
    service: 'finch-connect',
    env: process.env.NODE_ENV,
    version: buildInfo?.gitSha,
    proxy: 'https://rum.tryfinch.com',

    // Distributed tracing: propagate trace headers to the backend so browser
    // RUM sessions link to the api-server dd-trace spans in DataDog APM.
    // `(\/|$)` anchors the host boundary so lookalikes like
    // `api.tryfinch.com.evil.com` don't match and receive trace headers.
    allowedTracingUrls: [
      /^https:\/\/api\.tryfinch\.com(\/|$)/,
      /^https:\/\/api\.prod\.tryfinch\.com(\/|$)/,
    ],
    traceSampleRate: 100,

    // RUM + Session Replay: capture all sessions and record replay for 100% of
    // them. Mask all text/inputs by default — Connect collects payroll-provider
    // credentials (PII).
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    defaultPrivacyLevel: 'mask-user-input',
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
  });

  // Session Replay does NOT auto-start in browser-rum v4; the recorder must be
  // started explicitly or no replay segments are ever produced (PLAT-3454).
  // Harmless no-op once we move to browser-rum v5 (which auto-starts).
  datadogRum.startSessionReplayRecording();
}

amplitude.init(
  IS_PROD ? PRODUCTION_AMPLITUDE_KEY : DEVELOPMENT_AMPLITUDE_KEY,
  SESSION_KEY,
  {
    defaultTracking: true,
  },
);

const queryClient = new QueryClient();

const container = document.getElementById('root');

const root = createRoot(container);

root.render(
  <StrictMode>
    <ChakraProvider value={defaultSystem}>
      <FlagProvider config={unleashConfig}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </QueryClientProvider>
      </FlagProvider>
    </ChakraProvider>
  </StrictMode>,
);
